import { useEffect, useMemo, useRef, useState } from "react"

// --- Lib ---
import { getElementOverflowPosition } from "@/tiptap/lib/tiptap-collab-utils"

import { SuggestionMenu } from "@/tiptap/ui-utils/suggestion-menu"

import {
  createMediaInsertItem,
  filterSlashMenuItems,
  getSlashMenuNavigationItems,
  useSlashDropdownMenu,
} from "@/tiptap/ui/slash-dropdown-menu/use-slash-dropdown-menu"
import { ChevronRightIcon } from "@/tiptap/icons/chevron-right-icon"
import { MediaForm } from "@/tiptap/ui/media-form/media-form"

// --- UI Primitives ---
import { Button } from "@/tiptap/ui-primitive/button"
import { Separator } from "@/tiptap/ui-primitive/separator"
import {
  Card,
  CardBody,
  CardGroupLabel,
  CardItemGroup,
} from "@/tiptap/ui-primitive/card"


export const SlashDropdownMenu = (props) => {
  const { config, ...restProps } = props
  const { getSlashMenuItems } = useSlashDropdownMenu(config)

  return (
    <SuggestionMenu
      char="/"
      pluginKey="slashDropdownMenu"
      decorationClass="tiptap-slash-decoration"
      decorationContent="Filter..."
      selector="tiptap-slash-dropdown-menu"
      items={({ query, editor }) => {
        const showGroups = config?.showGroups !== false

        return filterSlashMenuItems(getSlashMenuItems(editor), query, showGroups)
      }}
      navigationItems={({ items, query }) => getSlashMenuNavigationItems(items, query)}
      {...restProps}>
      {(props) => <List {...props} config={config} />}
    </SuggestionMenu>
  );
}

const itemHasPanel = (item) => Boolean(item?.subItems?.length || item?.panel === "media")

const MediaPanel = ({ item, onSelect }) => {
  return (
    <Card
      className="tiptap-slash-card tiptap-slash-card--media"
      onPointerDown={(event) => event.stopPropagation()}>
      <CardBody className="tiptap-slash-card-body tiptap-slash-card-body--media">
        <CardItemGroup>
          <CardGroupLabel>Media</CardGroupLabel>
          <MediaForm
            onSubmit={(values) => {
              const mediaItem = createMediaInsertItem(values)
              if (!mediaItem) return false
              onSelect(mediaItem)
              return true
            }} />
        </CardItemGroup>
      </CardBody>
    </Card>
  )
}

const Item = (props) => {
  const { item, isSelected, onSelect, onOpenSubmenu } = props
  const itemRef = useRef(null)

  useEffect(() => {
    const selector = document.querySelector('[data-selector="tiptap-slash-dropdown-menu"]')
    if (!itemRef.current || !isSelected || !selector) return

    const overflow = getElementOverflowPosition(itemRef.current, selector)

    if (overflow === "top") {
      itemRef.current.scrollIntoView(true)
    } else if (overflow === "bottom") {
      itemRef.current.scrollIntoView(false)
    }
  }, [isSelected])

  useEffect(() => {
    if (isSelected && itemHasPanel(item)) {
      onOpenSubmenu(item)
    }
  }, [isSelected, item, onOpenSubmenu])

  const BadgeIcon = item.badge

  return (
    <Button
      ref={itemRef}
      className={item.className}
      variant="ghost"
      data-active-state={isSelected ? "on" : "off"}
      onMouseOver={() => {
        if (itemHasPanel(item)) {
          onOpenSubmenu(item)
        } else {
          onOpenSubmenu(null)
        }
      }}
      onClick={onSelect}>
      {BadgeIcon && <BadgeIcon className="tiptap-button-icon" />}
      <div className="tiptap-button-text">{item.title}</div>
      {itemHasPanel(item) ? <ChevronRightIcon className="tiptap-button-icon" /> : null}
    </Button>
  );
}

export const List = ({
  items,
  selectedIndex,
  selectedItem,
  onSelect,
  config
}) => {
  const [openPanelItem, setOpenPanelItem] = useState(null)
  const activeItem = selectedItem || items[selectedIndex]

  useEffect(() => {
    if (itemHasPanel(activeItem)) {
      setOpenPanelItem(activeItem)
    } else if (itemHasPanel(activeItem?.parentItem)) {
      setOpenPanelItem(activeItem.parentItem)
    } else {
      setOpenPanelItem(null)
    }
  }, [activeItem])

  const renderedItems = useMemo(() => {
    const rendered = []
    const showGroups = config?.showGroups !== false

    if (!showGroups) {
      items.forEach((item, index) => {
        rendered.push(<Item
          key={`item-${index}-${item.title}`}
          item={item}
          isSelected={item === activeItem}
          onOpenSubmenu={setOpenPanelItem}
          onSelect={() => {
            if (itemHasPanel(item)) {
              setOpenPanelItem(item)
              return
            }
            onSelect(item)
          }} />)
      })
      return rendered
    }

    let previousGroupLabel = null

    items.forEach((item, index) => {
      const groupLabel = item.group || ""

      if (groupLabel && groupLabel !== previousGroupLabel) {
        if (previousGroupLabel !== null) {
          rendered.push(<Separator key={`separator-${index}-${groupLabel}`} orientation="horizontal" />)
        }

        rendered.push(<CardGroupLabel key={`group-label-${index}-${groupLabel}`}>{groupLabel}</CardGroupLabel>)
      }

      rendered.push(
        <Item
          key={`item-${index}-${item.title}`}
          item={item}
          isSelected={item === activeItem}
          onOpenSubmenu={setOpenPanelItem}
          onSelect={() => {
            if (itemHasPanel(item)) {
              setOpenPanelItem(item)
              return
            }
            onSelect(item)
          }} />
      )

      previousGroupLabel = groupLabel
    })

    return rendered
  }, [items, activeItem, onSelect, config?.showGroups])

  if (!renderedItems.length) {
    return null
  }

  return (
    <div className="tiptap-slash-menu-row">
      <Card
        className="tiptap-slash-card"
        style={{
          maxHeight: "var(--suggestion-menu-max-height)",
        }}>
        <CardBody className="tiptap-slash-card-body">{renderedItems}</CardBody>
      </Card>
      {openPanelItem?.subItems?.length ? (
        <Card className="tiptap-slash-card">
          <CardBody className="tiptap-slash-card-body">
            <CardItemGroup>
              <CardGroupLabel>{openPanelItem.title}</CardGroupLabel>
              {openPanelItem.subItems.map((subItem) => {
                const BadgeIcon = subItem.badge
                const isSelected =
                  activeItem?.parentItem === openPanelItem &&
                  activeItem?.calloutVariant === subItem.calloutVariant

                return (
                  <Button
                    key={`${openPanelItem.title}-${subItem.title}`}
                    variant="ghost"
                    className="tiptap-slash-callout-sub-item"
                    data-callout-variant={subItem.calloutVariant}
                    data-active-state={isSelected ? "on" : "off"}
                    onClick={() => onSelect(subItem)}>
                    {BadgeIcon && <BadgeIcon className="tiptap-button-icon" />}
                    <div className="tiptap-button-text">{subItem.title}</div>
                  </Button>
                )
              })}
            </CardItemGroup>
          </CardBody>
        </Card>
      ) : null}
      {openPanelItem?.panel === "media" ? (
        <MediaPanel item={openPanelItem} onSelect={onSelect} />
      ) : null}
    </div>
  );
}
